import { useEffect, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Link, useSearchParams } from "react-router-dom";

import { AdminTabs } from "../components/AdminTabs";
import { KIND_LABEL, hostOf } from "../components/MemoryBriefing";
import { Button, Card, ErrorNote, InlineNote, Spinner, Tag } from "../components/primitives";
import { api } from "../lib/api";
import type { MemoryHit } from "../lib/types";

/**
 * Asking the knowledge graph a question, as the admin.
 *
 * Issue #69's evaluation box. Two routes, and which one is the *default*
 * carries the whole argument.
 *
 * **Search is the default and costs nothing.** The question an admin has about
 * memory is almost always "would a run find this", and that is answered by
 * running the retrieval a run gets — not by generating a query that resembles
 * it. It makes no model call and the question never leaves the process, which
 * is the standard `core/memory/CLAUDE.md` holds a hosted reranker to.
 *
 * **Cypher is opt-in per question**, because translating is one model call.
 * Behind a button rather than on every keystroke, and the statement is shown
 * *before* it runs — the difference between a console and a black box, and how
 * a wrong question gets noticed at all: a statement against a label that does
 * not exist is obviously wrong to a reader, where an empty result set is not.
 *
 * **A summary is not here** — it moved to the Memory page, which every user
 * has (#135); this console is for asking what a run would find.
 *
 * `?q=` asks a question on arrival — it is how a summary reference's "in
 * graph" link opens this page on that item.
 */
export function MemoryConsole() {
  const [params] = useSearchParams();
  const [question, setQuestion] = useState(() => params.get("q") ?? "");

  const ask = useMutation({ mutationFn: () => api.adminMemoryAsk(question) });
  const translate = useMutation({
    mutationFn: () => api.adminMemoryCypher({ question }),
  });

  useEffect(() => {
    // Searching is free, so a link carrying a question may run it.
    // Once, on arrival — not on every keystroke.
    if (question.trim()) ask.mutate();
  }, []);

  return (
    <div className="max-w-read space-y-4">
      <AdminTabs />
      <div>
        <p className="text-sm text-ink-2">
          Ask the knowledge graph in plain words and see what a run would get
          back: what the question is about, the answer it asked for —
          competitors, customers, figures — and what else matched, each with why
          it was admitted and where it came from. Searching is free and nothing
          leaves this machine. Turning the question into Cypher is one model
          call, so it is a separate button. A written summary of what memory
          holds is on the <Link to="/memory" className="text-accent">Memory</Link> page.
        </p>
      </div>

      <Card title="Ask">
        <div className="space-y-3 px-4 py-3">
          <textarea
            className="w-full rounded-sm border border-line bg-transparent px-2 py-1 text-sm"
            rows={2}
            placeholder="e.g. what do we know about heat sink suppliers in Germany?"
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
          />
          <div className="flex flex-wrap items-center gap-3">
            <Button
              variant="primary"
              disabled={!question.trim() || ask.isPending}
              onClick={() => ask.mutate()}
            >
              {ask.isPending ? "Searching…" : "Search memory"}
            </Button>
            <Button
              disabled={!question.trim() || translate.isPending}
              onClick={() => translate.mutate()}
            >
              {translate.isPending ? "Writing…" : "Write it as Cypher"}
            </Button>
            <span className="text-xs text-ink-2">
              Search is free. Cypher costs one model call and is shown before it
              runs.
            </span>
          </div>
          {ask.error && <ErrorNote error={ask.error} />}
          {translate.error && <ErrorNote error={translate.error} />}
        </div>
      </Card>

      {ask.data && <Answer hits={ask.data.hits} empty={ask.data.empty} />}
      {translate.data && <Statement drafted={translate.data} />}
      <Schema />
    </div>
  );
}

function Answer({ hits, empty }: { hits: MemoryHit[]; empty: boolean }) {
  // Three parts, because that is how the search now reads a question: what it
  // names, what it asked for of it, and what else matched its words.
  const subject = hits.filter((hit) => hit.role === "subject");
  const answer = hits.filter((hit) => hit.role === "answer");
  const context = hits.filter((hit) => !hit.role);
  return (
    <Card title={`What a run would see (${hits.length})`}>
      {empty ? (
        <p className="px-4 py-6 text-sm text-ink-2">
          Nothing matched. That is a fact about the graph rather than a failure
          of the search — if you expected a hit, the document may not have been
          indexed, which the Documents page reports per upload.
        </p>
      ) : (
        <div>
          {subject.length > 0 && <Group label="About" hits={subject} />}
          {answer.length > 0 && <Group label="Answer" hits={answer} />}
          {context.length > 0 && (
            <Group
              label={subject.length || answer.length ? "Also found" : "Found"}
              hits={context}
            />
          )}
        </div>
      )}
    </Card>
  );
}


function Group({ label, hits }: { label: string; hits: MemoryHit[] }) {
  return (
    <section>
      <h3 className="label border-b border-line bg-surface-2 px-4 py-1.5">{label}</h3>
      <ul className="divide-y divide-line">
        {hits.map((hit) => (
          <HitRow key={`${hit.kind}:${hit.key}`} hit={hit} />
        ))}
      </ul>
    </section>
  );
}

function HitRow({ hit }: { hit: MemoryHit }) {
  const [open, setOpen] = useState(false);
  // A figure reads as its number; the rest of its line is period and basis.
  const figure = hit.kind === "metric";
  const [value, ...detail] = figure ? hit.text.split(" · ") : [];
  const long = !figure && hit.text.length > 240;
  return (
    // The raw score stays available for whoever is tuning the search, and out
    // of the way of whoever is reading the answer: relevant and irrelevant
    // hits used to sit between 0.91 and 1.0, which said nothing.
    <li className="px-4 py-3" title={`score ${hit.score.toFixed(3)}`}>
      <div className="flex flex-wrap items-baseline gap-2">
        <Tag>{KIND_LABEL[hit.kind] ?? hit.kind}</Tag>
        <span className="min-w-0 text-sm font-medium text-ink">{hit.title}</span>
        {figure && (
          <span className="ml-auto font-mono text-sm tabular-nums text-ink">{value}</span>
        )}
      </div>
      {!figure && hit.text && (
        <p className={`mt-1 text-sm text-ink-2 ${open ? "" : "line-clamp-2"}`}>{hit.text}</p>
      )}
      <p className="mt-1 text-xs text-muted">
        {[
          hit.why,
          ...detail,
          hit.age_days != null ? `${hit.age_days}d old` : null,
          hit.confidence,
          hit.sector,
        ]
          .filter(Boolean)
          .join(" · ")}
      </p>
      <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs">
        {hit.sources.map((source) =>
          /^https?:\/\//.test(source) ? (
            <a
              key={source}
              href={source}
              target="_blank"
              rel="noreferrer"
              className="text-accent underline-offset-2 hover:underline"
            >
              {hostOf(source)}
            </a>
          ) : (
            <span key={source} className="text-muted">
              {source}
            </span>
          ),
        )}
        {hit.run_id && (
          <Link to={`/engagements/${encodeURIComponent(hit.run_id)}`} className="text-accent">
            open run
          </Link>
        )}
        {long && (
          <button type="button" onClick={() => setOpen((v) => !v)} className="text-muted underline">
            {open ? "less" : "more"}
          </button>
        )}
      </div>
    </li>
  );
}


function Statement({
  drafted,
}: {
  drafted: { cypher: string; refused?: string | null; cost_usd?: number };
}) {
  const [cypher, setCypher] = useState(drafted.cypher);
  const run = useMutation({ mutationFn: () => api.adminMemoryCypher({ cypher }) });

  return (
    <Card title="As Cypher">
      <div className="space-y-3 px-4 py-3">
        {/* Editable, because the reader who spots a wrong label should be able
            to fix it rather than re-ask and hope. */}
        <textarea
          className="w-full rounded-sm border border-line bg-transparent px-2 py-1 font-mono text-xs"
          rows={Math.min(10, cypher.split("\n").length + 1)}
          value={cypher}
          onChange={(e) => setCypher(e.target.value)}
        />
        {drafted.refused && (
          <InlineNote tone="warning">The guard would refuse this: {drafted.refused}</InlineNote>
        )}
        <div className="flex items-center gap-3">
          <Button
            variant="primary"
            disabled={run.isPending}
            onClick={() => run.mutate()}
          >
            {run.isPending ? "Running…" : "Run it"}
          </Button>
          {drafted.cost_usd != null && (
            <span className="font-mono text-xs tabular-nums text-muted">
              ${drafted.cost_usd.toFixed(4)} to write
            </span>
          )}
          <span className="text-xs text-ink-2">Read-only, and bounded.</span>
        </div>
        {run.error && <ErrorNote error={run.error} />}
        {run.data && <Rows result={run.data} />}
      </div>
    </Card>
  );
}

function Rows({
  result,
}: {
  result: {
    refused?: string | null;
    error?: string;
    rows?: Record<string, unknown>[];
    count?: number;
    truncated?: boolean;
    empty?: boolean;
  };
}) {
  // A refusal is a result with a reason, not a failure — it is what makes the
  // next attempt better rather than random.
  if (result.refused)
    return <InlineNote tone="warning">Refused: {result.refused}</InlineNote>;
  if (result.error) return <InlineNote tone="critical">Neo4j said: {result.error}</InlineNote>;
  if (result.empty)
    return (
      <p className="text-sm text-ink-2">
        No rows. If that is a surprise, check the labels against the schema
        below — asking for a label that does not exist returns nothing and looks
        exactly like nothing being there.
      </p>
    );

  const rows = result.rows ?? [];
  const columns = Object.keys(rows[0] ?? {});
  return (
    <div>
      {result.truncated && (
        <p className="mb-1 text-xs text-ink-2">
          Truncated at <span className="font-mono">{result.count}</span> rows — there may be more.
        </p>
      )}
      <div className="overflow-x-auto">
        <table className="w-full text-left text-xs">
          <thead className="text-muted">
            <tr>
              {columns.map((column) => (
                <th key={column} className="px-2 py-1 font-mono font-normal">
                  {column}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, index) => (
              <tr key={index} className="border-t border-line">
                {columns.map((column) => (
                  <td key={column} className="px-2 py-1 align-top">
                    {format(row[column])}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/** A cell, readably. The server keeps a node's readable fields only; a
 * figure reads as its measure, value, period and basis, anything else with a
 * name as that name. */
function format(value: unknown): string {
  if (value == null) return "—";
  if (Array.isArray(value)) return value.map(format).join(", ");
  if (typeof value === "object") {
    const node = value as Record<string, unknown>;
    if (node.value != null && (node.measure != null || node.currency != null)) {
      const amount =
        typeof node.value === "number" ? node.value.toLocaleString() : String(node.value);
      return [node.measure ?? node.name, [node.currency, amount].filter(Boolean).join(" "),
        node.validTo, node.basis !== "unknown" ? node.basis : null]
        .filter((part) => part != null && part !== "")
        .join(" · ");
    }
    if (typeof node.name === "string") return node.name;
    return JSON.stringify(value);
  }
  return String(value);
}

/** The vocabulary, on the same page as the box — because "no rows" and "that
 * label does not exist" are the same screen until you can check. */
function Schema() {
  const [open, setOpen] = useState(false);
  const schema = useQuery({
    queryKey: ["admin-memory-schema"],
    queryFn: api.adminMemorySchema,
    enabled: open,
  });

  return (
    <Card
      title="Schema"
      action={
        <Button size="sm" onClick={() => setOpen(!open)}>
          {open ? "Hide" : "Show"}
        </Button>
      }
    >
      {open && (
        <div className="px-4 py-3">
          {schema.isLoading && <Spinner />}
          {schema.error && <ErrorNote error={schema.error} />}
          {schema.data && (
            <pre className="overflow-x-auto whitespace-pre-wrap text-xs text-ink-2">
              {schema.data.schema}
            </pre>
          )}
        </div>
      )}
    </Card>
  );
}
